import fetcher from '@/services/fetcher'
import { toApiUrl } from '@/utils/url-generation-utils'

export type TrustpilotStats = {
  total_reviews: number
  rating: number
  trust_score: number
  profile_url: string | null
}

const STORAGE_KEY = 'trustpilot_stats'
let fetchPromise: Promise<TrustpilotStats | null> | null = null

/**
 * Service to handle Trustpilot stats storage and retrieval
 */
export const TrustpilotStorage = {
  /**
   * Store stats in sessionStorage
   */
  storeStats(stats: TrustpilotStats): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stats))
    } catch (error) {
      console.error('Failed to store Trustpilot stats:', error)
    }
  },

  /**
   * Get stats from sessionStorage if they exist
   */
  getStoredStats(): TrustpilotStats | null {
    try {
      const storedData = sessionStorage.getItem(STORAGE_KEY)

      if (!storedData) {
        return null
      }

      const parsedData = JSON.parse(storedData) as TrustpilotStats

      // Check if total reviews exists and is number type
      if (typeof parsedData.total_reviews !== 'number') {
        return null
      }

      return parsedData
    } catch (error) {
      console.error('Failed to get stored Trustpilot stats:', error)
      return null
    }
  },

  /**
   * Fetch stats from the API
   */
  async fetchStats(): Promise<TrustpilotStats | null> {
    try {
      const result = await fetcher.get<TrustpilotStats>(
        toApiUrl('trustpilot/stats'),
        false
      )

      if (result.data.value) {
        this.storeStats(result.data.value)
        return result.data.value
      }

      return null
    } catch (error) {
      console.error('Failed to fetch Trustpilot stats:', error)
      return null
    }
  },

  /**
   * Get stats from cache or fetch from API
   */
  async getStats(): Promise<TrustpilotStats | null> {
    const existingStats = this.getStoredStats()

    if (existingStats) {
      return existingStats
    }

    if (fetchPromise) {
      return fetchPromise
    }

    fetchPromise = this.fetchStats().finally(() => {
      fetchPromise = null
    })

    return fetchPromise
  },
}
